import React from 'react';
import { cn } from '@/lib/utils';
import { Link, useNavigate, useLocation } from 'react-router-dom';


interface NavItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick?: () => void;
}

const NavItem = ({ to, label, icon, isActive, onClick }: NavItemProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if the link is an anchor link
  const isAnchorLink = to.includes('#');
  // Check if we're on an auth page
  const isAuthPage = location.pathname === '/signin' || location.pathname === '/signup' || location.pathname === '/verify-email';

  const handleClick = (e: React.MouseEvent) => {
    // For regular links within the app, use React Router's navigate
    // to prevent full page refresh
    if (!isAnchorLink && !isAuthPage) {
      e.preventDefault();
      
      // Normal navigation for all routes
      navigate(to);
      
      if (onClick) onClick();
      return;
    }
    
    if (isAnchorLink) {
      e.preventDefault();
      
      // If on auth page, first navigate to home page then scroll to anchor
      if (isAuthPage) {
        navigate('/');
        
        // After navigation, scroll to the anchor
        const targetId = to.split('#')[1];
        setTimeout(() => {
          if (targetId) {
            const element = document.getElementById(targetId);
            if (element) {
              // CSS-based responsive offset calculation
              const offset = window.innerWidth < 640 ? 0 : 80; // Mobile vs desktop navbar height
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - offset;
              
              window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
              });
            }
          } else {
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
          }
        }, 100); // Small delay to ensure the home page is mounted
      } else {
        // Normal anchor link behavior when not on auth pages
        const targetId = to.split('#')[1];
        if (targetId) {
          const element = document.getElementById(targetId);
          if (element) {
            // CSS-based responsive offset calculation
            const offset = window.innerWidth < 640 ? 0 : 80; // Mobile vs desktop navbar height
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }
        } else {
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        }
      }
    } else if (isAuthPage) {
      // For non-anchor links on auth pages, navigate to the specified route
      e.preventDefault();
      navigate(to);
    }
    
    if (onClick) onClick();
  };

  return (
    <>
      {isAnchorLink ? (
        <a 
          href={to}
          className={cn(
            'group relative flex items-center transition-all duration-300',
            // CSS-only responsive layout - vertical on mobile, horizontal on larger screens
            'flex-col justify-center px-1 py-1 sm:flex-row sm:gap-3 sm:px-4 sm:py-1',
            'h-[40px] w-[48px] sm:h-auto sm:w-auto sm:min-h-[40px]', // Fixed mobile size, auto on larger screens
            isActive 
              ? 'text-brandBlue dark:text-brightPurple font-medium'
              : 'text-brandBlue/80 dark:text-brightPurple/80 hover:text-brandBlue dark:hover:text-brightPurple'
          )}
          onClick={handleClick}
        >
          {/* Dynamic background gradient */}
          <div className={cn(
            "absolute inset-0 rounded-md transition-opacity duration-300",
            isActive 
              ? 'opacity-100 bg-gradient-to-br from-brandBlue/15 to-brandPurple/10 dark:from-brandBlue/25 dark:to-brightPurple/20 backdrop-blur-sm'
              : 'opacity-0 group-hover:opacity-100 bg-gradient-to-br from-brandBlue/10 to-brandPurple/5 dark:from-brandBlue/15 dark:to-brightPurple/10'
          )} />

          {/* Icon with hover effect - centered both vertically and horizontally */}
          <div className={cn(
            'relative z-10 flex items-center justify-center transition-transform duration-300',
            'w-8 h-8 aspect-square', // Consistent icon container size
            isActive 
              ? 'scale-110 text-brandBlue dark:text-brightPurple'
              : 'text-brandBlue/80 dark:text-brightPurple/80 group-hover:text-brandBlue dark:group-hover:text-brightPurple group-hover:scale-110'
          )}>
            {icon}
          </div>

          {/* Label - CSS-only responsive: hidden on mobile (< sm), visible on sm+ */}
          <span className="hidden sm:inline relative z-10 text-sm font-medium">
              {label}
            </span>
        </a>
      ) : (
        <Link 
          to={to}
          className={cn(
            'group relative flex items-center transition-all duration-300',
            // CSS-only responsive layout - vertical on mobile, horizontal on larger screens
            'flex-col justify-center px-1 py-1 sm:flex-row sm:gap-3 sm:px-4 sm:py-1',
            'min-w-[40px] max-w-[48px] sm:min-w-auto sm:max-w-none sm:min-h-[40px]', // Fixed mobile size, auto on larger screens
            isActive 
              ? 'text-brandBlue dark:text-brightPurple font-medium'
              : 'text-brandBlue/80 dark:text-brightPurple/80 hover:text-brandBlue dark:hover:text-brightPurple'
          )}
          onClick={handleClick}
        >
          {/* Dynamic background gradient */}
          <div className={cn(
            "absolute inset-0 rounded-md transition-opacity duration-300",
            isActive 
              ? 'opacity-100 bg-gradient-to-br from-brandBlue/15 to-brandPurple/10 dark:from-brandBlue/25 dark:to-brightPurple/20 backdrop-blur-sm'
              : 'opacity-0 group-hover:opacity-100 bg-gradient-to-br from-brandBlue/10 to-brandPurple/5 dark:from-brandBlue/15 dark:to-brightPurple/10'
          )} />

          {/* Icon with hover effect - centered both vertically and horizontally */}
          <div className={cn(
            'relative z-10 flex items-center justify-center transition-transform duration-300',
            'w-8 h-8 aspect-square', // Consistent icon container size
            isActive 
              ? 'scale-110 text-brandBlue dark:text-brightPurple'
              : 'text-brandBlue/80 dark:text-brightPurple/80 group-hover:text-brandBlue dark:group-hover:text-brightPurple group-hover:scale-110'
          )}>
            {icon}
          </div>

          {/* Label - CSS-only responsive: hidden on mobile (< sm), visible on sm+ */}
          <span className="hidden sm:inline relative z-10 text-sm font-medium">
              {label}
            </span>
        </Link>
      )}
    </>
  );
};

export default NavItem;
